import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { HintErrorComponent } from "@vg-constellation/angular-17/hint-error";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";

import { Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";

import { WebLogger } from "@weps/shared-http";
import { RobService } from "@services/rob/rob.service";
import { FinancialInstitution } from "@models/rob.model";
import { DEFAULT_FINANCIAl_INSTITUTION_ID } from "@services/query/query";
import * as content from "@services/content/guided-flow/account-details-content";

export interface InstitutionSelection {
  financialInstitutionId: string;
  financialInstitutionName?: string;
}

const OTHER_INSTITUTION: FinancialInstitution = {
  financialInstitutionId: DEFAULT_FINANCIAl_INSTITUTION_ID,
  financialInstitutionName: content.INSTITUTION_OTHER_NAME,
};

@Component({
  selector: "app-institution-typeahead",
  standalone: true,
  imports: [CommonModule, FormsModule, HintErrorComponent, InputComponent, LabelComponent],
  templateUrl: "./institution-typeahead.component.html",
  styleUrls: ["./institution-typeahead.component.scss"],
})
export class InstitutionTypeaheadComponent implements OnInit, OnDestroy {
  @Input() initialInstitutionId?: string;
  @Input() initialInstitutionName?: string;

  @Output() institutionSelected = new EventEmitter<InstitutionSelection>();
  @Output() validationStateChange = new EventEmitter<boolean>();

  protected readonly content = content;
  protected readonly OTHER_INSTITUTION_ID = DEFAULT_FINANCIAl_INSTITUTION_ID;

  searchInput = "";
  results: FinancialInstitution[] = [];
  showDropdown = false;
  isFocused = false;
  isLoading = false;
  searchError = false;
  selectedInstitution: FinancialInstitution | null = null;
  isOtherSelected = false;
  otherName = "";
  validOtherName = true;

  private searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private robService: RobService) {}

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term) => {
          if (term.length >= 3) {
            this.isLoading = true;
            this.searchError = false;
            this.showDropdown = true;
          } else {
            this.isLoading = false;
          }
        }),
        filter((term) => term.length >= 3),
        switchMap((term) =>
          this.robService.searchInstitutions(term).pipe(
            catchError((err) => {
              WebLogger.log({
                message: "Institution search failed",
                level: "error",
                payload: { err },
              });
              this.searchError = true;
              return of([]);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((institutions) => {
        this.isLoading = false;
        this.results = [...institutions, OTHER_INSTITUTION];
        this.showDropdown = true;
      });

    if (this.initialInstitutionId) {
      this.prepopulate(this.initialInstitutionId, this.initialInstitutionName);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private prepopulate(id: string, name?: string): void {
    if (id === DEFAULT_FINANCIAl_INSTITUTION_ID) {
      this.isOtherSelected = true;
      this.searchInput = content.INSTITUTION_OTHER_NAME;
      this.otherName = name || "";
    } else {
      this.searchInput = name || "";
      this.selectedInstitution = {
        financialInstitutionId: id,
        financialInstitutionName: name || "",
      };
    }
  }

  onSearchInput(value: string): void {
    this.searchInput = value;
    this.selectedInstitution = null;
    this.isOtherSelected = false;
    this.searchError = false;
    if (value.length < 3) {
      this.isLoading = false;
      this.results = [];
      this.showDropdown = this.isFocused;
    }
    this.searchTerm$.next(value);
  }

  onSearchFocus(): void {
    this.isFocused = true;
    this.showDropdown = true;
  }

  onSearchBlur(): void {
    this.isFocused = false;
    setTimeout(() => {
      this.showDropdown = false;
    }, 150);
  }

  selectInstitution(institution: FinancialInstitution): void {
    this.showDropdown = false;

    if (institution.financialInstitutionId === DEFAULT_FINANCIAl_INSTITUTION_ID) {
      this.isOtherSelected = true;
      this.selectedInstitution = null;
      this.searchInput = institution.financialInstitutionName;
      this.emitSelection();
      return;
    }

    this.isOtherSelected = false;
    this.otherName = "";
    this.selectedInstitution = institution;
    this.searchInput = institution.financialInstitutionName;
    this.emitSelection();
  }

  onOtherNameInput(value: string): void {
    this.otherName = value;
    if (value.trim()) {
      this.validOtherName = true;
      this.validationStateChange.emit(true);
    }
    this.emitSelection();
  }

  onOtherNameBlur(): void {
    this.validOtherName = !!this.otherName.trim();
    this.validationStateChange.emit(this.validOtherName);
  }

  hasError(): boolean {
    if (this.isOtherSelected) {
      this.validOtherName = !!this.otherName.trim();
      this.validationStateChange.emit(this.validOtherName);
      return !this.validOtherName;
    }
    return false;
  }

  private emitSelection(): void {
    if (this.isOtherSelected) {
      this.institutionSelected.emit({
        financialInstitutionId: DEFAULT_FINANCIAl_INSTITUTION_ID,
        financialInstitutionName: this.otherName,
      });
    } else if (this.selectedInstitution) {
      this.institutionSelected.emit({
        financialInstitutionId: this.selectedInstitution.financialInstitutionId,
      });
    }
  }
}
